import { XMarkIcon } from '@heroicons/react/24/outline';

interface ToastMessage {
    id: string;
    message: string;
    type: 'success' | 'error';
}

interface ToastProps {
    toast: ToastMessage;
    onDismiss: (id: string) => void;
}

interface ToastStackProps {
    toasts: readonly ToastMessage[];
    onDismiss: (id: string) => void;
}

function Toast({ toast, onDismiss }: ToastProps) {
    const isSuccess = toast.type === 'success';
    const bgColor = isSuccess ? 'bg-[#CAF2ED]' : 'bg-red-50';
    const borderColor = isSuccess ? 'border-[#5CC6BB]' : 'border-red-300';
    const textColor = isSuccess ? 'text-[#2C6F67]' : 'text-red-800';
    const iconColor = isSuccess ? 'text-[#5CC6BB]' : 'text-red-500';

    return (
        <div
            className={`${bgColor} ${borderColor} border-2 rounded-full px-4 py-3 flex items-center gap-3 min-w-[300px] max-w-md shadow-lg`}
        >
            <div className={`flex-shrink-0 ${iconColor}`}>
                {isSuccess ? (
                    <svg
                        className="w-5 h-5"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle
                            cx="10"
                            cy="10"
                            r="9"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                        />
                        <path
                            d="M6 10l2 2 4-4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                        />
                    </svg>
                ) : (
                    <svg
                        className="w-5 h-5"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle
                            cx="10"
                            cy="10"
                            r="9"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                        />
                        <path
                            d="M7 7l6 6M13 7l-6 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                )}
            </div>
            <p className={`flex-1 ${textColor} font-medium text-sm`}>{toast.message}</p>
            <button
                onClick={() => onDismiss(toast.id)}
                className={`flex-shrink-0 ${iconColor} hover:opacity-70 transition-opacity`}
                aria-label="Close"
            >
                <XMarkIcon className="w-4 h-4" />
            </button>
        </div>
    );
}

export function ToastStack({ toasts, onDismiss }: ToastStackProps) {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2">
            {toasts.map((toast) => (
                <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
            ))}
        </div>
    );
}
